import React from "react";
import { motion } from "framer-motion";

// Animated stylized "cuadro eléctrico" / circuit board illustration in SVG
export default function CircuitPanel() {
  return (
    <div className="relative w-full aspect-square max-w-[560px] mx-auto lg:mx-0">
      {/* Ambient glow */}
      <div className="absolute inset-0 rounded-full blur-3xl opacity-40 bg-[radial-gradient(circle_at_60%_40%,#00E5FF_0%,transparent_55%)]" />
      <div className="absolute inset-0 rounded-full blur-3xl opacity-25 bg-[radial-gradient(circle_at_30%_70%,#E57C23_0%,transparent_60%)]" />

      <svg
        viewBox="0 0 500 500"
        className="relative w-full h-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        {/* Outer frame */}
        <rect x="40" y="40" width="420" height="420" rx="24" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
        <rect x="60" y="60" width="380" height="380" rx="18" stroke="rgba(0,229,255,0.18)" strokeWidth="1" strokeDasharray="2 6" />

        {/* Grid */}
        {Array.from({ length: 7 }).map((_, i) => (
          <line
            key={`v${i}`}
            x1={100 + i * 50}
            y1={80}
            x2={100 + i * 50}
            y2={420}
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        ))}
        {Array.from({ length: 7 }).map((_, i) => (
          <line
            key={`h${i}`}
            x1={80}
            y1={100 + i * 50}
            x2={420}
            y2={100 + i * 50}
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        ))}

        {/* Bus bar */}
        <rect x="90" y="140" width="320" height="8" rx="2" fill="url(#copperGrad)" opacity="0.9" />
        <rect x="90" y="352" width="320" height="8" rx="2" fill="url(#cyanGrad)" opacity="0.8" />

        {/* Circuit breakers row */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <g key={i} transform={`translate(${112 + i * 50}, 175)`}>
            <rect x="0" y="0" width="30" height="60" rx="4" fill="#121620" stroke="rgba(255,255,255,0.12)" />
            <rect x="12" y="8" width="6" height="16" rx="2" fill="#E57C23" opacity={0.85} />
            <circle cx="15" cy="46" r="3" fill="#00E5FF">
              <animate
                attributeName="opacity"
                values={`0.2; 1; 0.2`}
                dur={`${1.5 + i * 0.3}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ))}

        {/* Wires flowing */}
        <path
          d="M90 148 C 90 220, 180 240, 180 310 C 180 340, 250 340, 250 356"
          stroke="#00E5FF"
          strokeWidth="1.5"
          className="circuit-path"
        />
        <path
          d="M410 148 C 410 220, 320 240, 320 310 C 320 340, 260 340, 260 356"
          stroke="#E57C23"
          strokeWidth="1.5"
          className="circuit-path"
        />
        <path
          d="M250 60 L 250 130"
          stroke="rgba(0,229,255,0.6)"
          strokeWidth="1.5"
          className="circuit-path"
        />
        <path
          d="M250 360 L 250 440"
          stroke="rgba(229,124,35,0.6)"
          strokeWidth="1.5"
          className="circuit-path"
        />

        {/* Corner nodes */}
        {[
          [60, 60], [440, 60], [60, 440], [440, 440],
        ].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="4" fill="#00E5FF" />
            <circle cx={x} cy={y} r="10" stroke="#00E5FF" strokeOpacity="0.4" />
          </g>
        ))}

        {/* Central node */}
        <g>
          <circle cx="250" cy="250" r="28" fill="#0A0D14" stroke="#E57C23" strokeWidth="1.5" />
          <circle cx="250" cy="250" r="14" fill="#E57C23" opacity="0.15" />
          <path
            d="M244 240 L253 248 L247 251 L256 262"
            stroke="#E57C23"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* Labels */}
        <text x="82" y="130" fill="rgba(255,255,255,0.35)" fontFamily="JetBrains Mono" fontSize="9" letterSpacing="2">
          BUS · 230V
        </text>
        <text x="82" y="382" fill="rgba(255,255,255,0.35)" fontFamily="JetBrains Mono" fontSize="9" letterSpacing="2">
          NEUTRO · N
        </text>

        <defs>
          <linearGradient id="copperGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#E57C23" stopOpacity="0.2" />
            <stop offset="0.5" stopColor="#FF8A00" stopOpacity="1" />
            <stop offset="1" stopColor="#E57C23" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="cyanGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#00E5FF" stopOpacity="0.15" />
            <stop offset="0.5" stopColor="#00E5FF" stopOpacity="0.9" />
            <stop offset="1" stopColor="#00E5FF" stopOpacity="0.15" />
          </linearGradient>
        </defs>
      </svg>

      {/* Floating chip badges */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-6 -right-2 md:right-4 rounded-xl border border-white/10 bg-[#0A0D14]/80 backdrop-blur px-3 py-2 flex items-center gap-2"
      >
        <span className="h-2 w-2 rounded-full bg-[#00E5FF] shadow-[0_0_10px_#00E5FF]" />
        <span className="font-mono text-[11px] text-white/80">230V · ESTABLE</span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="absolute bottom-8 -left-2 md:left-6 rounded-xl border border-white/10 bg-[#0A0D14]/80 backdrop-blur px-3 py-2"
      >
        <div className="font-mono text-[10px] text-[#9CA3AF]">CERT · REBT</div>
        <div className="font-mono text-[10px] text-[#E57C23]">ITC-BT-40</div>
      </motion.div>
    </div>
  );
}
